import moment from "moment";
import { supabase } from "./supabaseClient";

const FORM_SELECT =
  "id, title, description, schema, settings, is_published, published_at, updated_at";

/**
 * Published global evaluation form (v1: one form for all courses).
 * Fallback: null when unpublished / missing.
 */
export async function fetchPublishedEvaluationForm() {
  const { data, error } = await supabase
    .from("course_evaluation_form")
    .select(FORM_SELECT)
    .eq("is_published", true)
    .order("published_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("fetchPublishedEvaluationForm:", error);
    return null;
  }
  return data || null;
}

export function normalizeEvaluationSchema(raw) {
  if (!raw || typeof raw !== "object") return { sections: [] };
  const sections = Array.isArray(raw.sections) ? raw.sections : [];
  return {
    sections: sections.map((section, sIdx) => ({
      id: section?.id || `section_${sIdx}`,
      title: section?.title || "Section",
      description: section?.description || "",
      fields: Array.isArray(section?.fields)
        ? section.fields.map((field, fIdx) => ({
            id: field?.id || `field_${fIdx}`,
            type: field?.type || "text",
            label: field?.label || "Question",
            required: Boolean(field?.required),
            helpText: field?.helpText || "",
            placeholder: field?.placeholder || "",
            bindTo: field?.bindTo || null,
            options: Array.isArray(field?.options) ? field.options : [],
            scale: Array.isArray(field?.scale) ? field.scale : [],
            rows: Array.isArray(field?.rows) ? field.rows : [],
          }))
        : [],
    })),
  };
}

export function buildFormSnapshot(form) {
  return {
    title: form?.title || "Course Evaluation Form",
    description: form?.description ?? null,
    schema: normalizeEvaluationSchema(form?.schema),
    settings: form?.settings || {
      availability: { mode: "relative_to_programme_end", offset_days: 0 },
    },
  };
}

/**
 * When students can fill the evaluation for a course run.
 * - when_published: anytime the form is published
 * - relative_to_programme_end: from (end_date + offset_days); offset 0 = from end date
 */
export function isEvaluationOpenForCourseRun(form, courseRun, now = moment()) {
  if (!form?.is_published) return false;
  const mode = form?.settings?.availability?.mode || "when_published";
  if (mode !== "relative_to_programme_end") return true;

  const endDate = courseRun?.end_date;
  if (!endDate) return false;
  const offset =
    typeof form?.settings?.availability?.offset_days === "number"
      ? form.settings.availability.offset_days
      : 0;
  const opensAt = moment(endDate).startOf("day").add(offset, "days");
  return now.clone().startOf("day").isSameOrAfter(opensAt);
}

export function formatEvaluationOpensLabel(form, courseRun) {
  if (!form || isEvaluationOpenForCourseRun(form, courseRun)) return null;
  const mode = form?.settings?.availability?.mode;
  if (mode !== "relative_to_programme_end" || !courseRun?.end_date) {
    return "Evaluation is not open yet.";
  }
  const offset =
    typeof form?.settings?.availability?.offset_days === "number"
      ? form.settings.availability.offset_days
      : 0;
  const opensAt = moment(courseRun.end_date).startOf("day").add(offset, "days");
  return `Evaluation opens on ${opensAt.format("DD MMM YYYY")}.`;
}

/**
 * Full certification / programme title for the student's cert run.
 */
export async function fetchFullCertificationName(registration) {
  const certRunId = registration?.draft_cert_run_code;
  if (!certRunId) return "";

  const { data, error } = await supabase
    .from("full_certification_runs")
    .select("full_certification ( name )")
    .eq("id", certRunId)
    .maybeSingle();

  if (error) {
    console.error("fetchFullCertificationName:", error);
    return "";
  }
  return data?.full_certification?.name || "";
}

/**
 * Resolve autofill context from course run + registration (+ optional full cert name).
 */
export function buildEvaluationContext({
  courseRun,
  registration,
  fullCertificationName = "",
}) {
  const moduleName =
    courseRun?.course_type?.name ||
    courseRun?.course_type_name ||
    courseRun?.course_type_code ||
    "";
  const instructor =
    courseRun?.instructor_name ||
    courseRun?.trainers?.name ||
    "";
  const endMonth = courseRun?.end_date
    ? moment(courseRun.end_date).format("MMM YYYY")
    : "";

  return {
    "full_certification.name": fullCertificationName || "",
    "course_type.name": moduleName,
    "course_run.id": courseRun?.id || "",
    "course_run.module_name": moduleName,
    "course_run.instructor_name": instructor,
    "course_run.end_month": endMonth,
    "registration.participant_name": registration?.participant_name || "",
    "registration.participant_email": registration?.participant_email || "",
    "registration.mobile_no": registration?.mobile_no || "",
  };
}

export function seedAnswersFromContext(schema, context) {
  const answers = {};
  for (const section of schema.sections || []) {
    for (const field of section.fields || []) {
      if (!field.bindTo) continue;
      const value = context[field.bindTo];
      if (value == null || value === "") continue;
      if (field.type === "name") {
        answers[field.id] = { first: String(value), last: "" };
      } else {
        answers[field.id] = String(value);
      }
    }
  }
  return answers;
}

export async function fetchEvaluationResponse({ formId, registrationId, courseRunId }) {
  if (!formId || !registrationId || !courseRunId) return null;
  const { data, error } = await supabase
    .from("course_evaluation_response")
    .select(
      "id, form_id, registration_id, course_run_id, user_id, form_snapshot, answers, context_snapshot, submitted_at"
    )
    .eq("form_id", formId)
    .eq("registration_id", registrationId)
    .eq("course_run_id", courseRunId)
    .maybeSingle();

  if (error) {
    console.error("fetchEvaluationResponse:", error);
    return null;
  }
  return data || null;
}

/**
 * Course run IDs that already have a submission for any of the given registrations.
 */
export async function fetchSubmittedCourseRunIds({ formId, registrationIds }) {
  if (!formId || !registrationIds?.length) return new Set();
  const { data, error } = await supabase
    .from("course_evaluation_response")
    .select("course_run_id")
    .eq("form_id", formId)
    .in("registration_id", registrationIds);

  if (error) {
    console.error("fetchSubmittedCourseRunIds:", error);
    return new Set();
  }
  return new Set((data || []).map((row) => row.course_run_id).filter(Boolean));
}

/**
 * Find the student's registration linked to this course run.
 * Order: registration_modules → draft_course_run_code → full_cert_many_course_run.
 * Pass preview student userId/email when staff is using View as student.
 */
export async function resolveRegistrationForCourseRun({ userId, email, courseRunId }) {
  if (!courseRunId || (!userId && !email)) return null;

  const registrationSelect =
    "id, user_id, participant_email, participant_name, mobile_no, draft_cert_run_code, draft_course_run_code, registration_stage";
  const byId = new Map();

  if (userId) {
    const { data, error } = await supabase
      .from("registration")
      .select(registrationSelect)
      .eq("user_id", userId)
      .eq("registration_stage", "payment_received");
    if (error) console.error("resolveRegistrationForCourseRun user_id:", error);
    (data || []).forEach((r) => byId.set(r.id, r));
  }
  if (email) {
    const { data, error } = await supabase
      .from("registration")
      .select(registrationSelect)
      .ilike("participant_email", email)
      .eq("registration_stage", "payment_received");
    if (error) console.error("resolveRegistrationForCourseRun email:", error);
    (data || []).forEach((r) => {
      if (!byId.has(r.id)) byId.set(r.id, r);
    });
  }

  const registrations = Array.from(byId.values());
  if (registrations.length === 0) return null;

  const registrationIds = registrations.map((r) => r.id);

  // 1) Direct module link (most reliable for full-cert students)
  const { data: moduleLinks, error: moduleError } = await supabase
    .from("registration_modules")
    .select("registration_id, course_run_code")
    .in("registration_id", registrationIds)
    .eq("course_run_code", courseRunId);

  if (moduleError) {
    console.error("resolveRegistrationForCourseRun registration_modules:", moduleError);
  } else if (moduleLinks?.length) {
    const matchId = moduleLinks[0].registration_id;
    const hit = registrations.find((r) => r.id === matchId);
    if (hit) return hit;
  }

  // 2) Standalone course registration
  const standalone = registrations.find((r) => r.draft_course_run_code === courseRunId);
  if (standalone) return standalone;

  // 3) Cert run → course run mapping
  const certIds = [
    ...new Set(registrations.map((r) => r.draft_cert_run_code).filter(Boolean)),
  ];
  if (certIds.length === 0) return null;

  const { data: links, error } = await supabase
    .from("full_cert_many_course_run")
    .select("cert_run_code, course_run_code")
    .in("cert_run_code", certIds)
    .eq("course_run_code", courseRunId);

  if (error) {
    console.error("resolveRegistrationForCourseRun full_cert_many_course_run:", error);
    return null;
  }

  const matchingCertIds = new Set((links || []).map((l) => l.cert_run_code));
  return (
    registrations.find((r) => matchingCertIds.has(r.draft_cert_run_code)) || null
  );
}

export async function submitEvaluationResponse({
  form,
  registrationId,
  courseRunId,
  userId,
  answers,
  contextSnapshot,
}) {
  if (!form?.id || !registrationId || !courseRunId) {
    throw new Error("Missing form, registration, or course run.");
  }

  const payload = {
    form_id: form.id,
    registration_id: registrationId,
    course_run_id: courseRunId,
    user_id: userId || null,
    form_snapshot: buildFormSnapshot(form),
    answers: answers || {},
    context_snapshot: contextSnapshot || {},
    submitted_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from("course_evaluation_response")
    .insert(payload)
    .select("id, submitted_at")
    .single();

  if (error) throw error;
  return data;
}

export function validateEvaluationAnswers(schema, answers) {
  const missing = [];
  for (const section of schema.sections || []) {
    for (const field of section.fields || []) {
      if (!field.required) continue;
      const value = answers[field.id];
      if (field.type === "rating_matrix") {
        const map = value && typeof value === "object" ? value : {};
        const rows = field.rows || [];
        const incomplete = rows.some((row) => !map[row.id]);
        if (incomplete || rows.length === 0) missing.push(field.label);
        continue;
      }
      if (field.type === "name") {
        const n = value && typeof value === "object" ? value : {};
        if (!String(n.first || "").trim() && !String(n.last || "").trim()) {
          missing.push(field.label);
        }
        continue;
      }
      if (value == null || String(value).trim() === "") missing.push(field.label);
    }
  }
  return missing;
}

/** Display label for Course Progress when evaluation is outstanding. */
export const EVALUATION_DUE_LABEL = "Evaluation due";

export function getEvaluationDueBadgeColors() {
  return { bg: "orange.400", color: "white" };
}
